import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LayoutDashboard, Briefcase, Award, Wallet, Trophy, Bell, User, LogOut,
  ShoppingBag, ShieldCheck, Users as UsersIcon, Send, ClipboardList, Menu, X, Link2
} from "lucide-react";
import api from "@/lib/api";

const NavItem = ({ to, icon: Icon, label, testid, end = false }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all ${
        isActive
          ? "bg-[#ff7a00]/10 text-[#ff7a00] border border-[#ff7a00]/30 glow-orange-soft"
          : "text-[#cfcfcf] hover:bg-white/5 hover:text-[#ff9a2f] border border-transparent"
      }`
    }
  >
    <Icon size={17} />
    <span className="font-medium">{label}</span>
  </NavLink>
);

export const DashboardLayout = ({ admin = false }) => {
  const { user, logout, isAdmin } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);

  useEffect(() => {
    if (!admin) {
      api.get("/notifications").then((r) => setNotifs(r.data || [])).catch(() => {});
    }
  }, [admin]);

  const unread = notifs.filter((n) => !n.read).length;

  const traderNav = (
    <>
      <NavItem to="/dashboard" icon={LayoutDashboard} label="Overview" testid="nav-overview" end />
      <NavItem to="/dashboard/accounts" icon={Briefcase} label="Trading Accounts" testid="nav-accounts" />
      <NavItem to="/dashboard/orders" icon={ShoppingBag} label="My Orders" testid="nav-orders" />
      <NavItem to="/dashboard/certificates" icon={Award} label="Certificates" testid="nav-certificates" />
      <NavItem to="/dashboard/payouts" icon={Wallet} label="Payouts" testid="nav-payouts" />
      <NavItem to="/dashboard/affiliate" icon={Link2} label="Affiliate" testid="nav-affiliate-dash" />
      <NavItem to="/dashboard/leaderboard" icon={Trophy} label="Leaderboard" testid="nav-leaderboard" />
      <NavItem to="/dashboard/profile" icon={User} label="Profile" testid="nav-profile" />
    </>
  );

  const adminNav = (
    <>
      <NavItem to="/admin" icon={LayoutDashboard} label="Admin Overview" testid="admin-nav-overview" end />
      <NavItem to="/admin/traders" icon={UsersIcon} label="Traders" testid="admin-nav-traders" />
      <NavItem to="/admin/orders" icon={ClipboardList} label="Orders" testid="admin-nav-orders" />
      <NavItem to="/admin/accounts" icon={Briefcase} label="Trading Accounts" testid="admin-nav-accounts" />
      <NavItem to="/admin/payouts" icon={Wallet} label="Payouts" testid="admin-nav-payouts" />
      <NavItem to="/admin/certificates" icon={Award} label="Certificates" testid="admin-nav-certificates" />
      <NavItem to="/admin/broadcast" icon={Send} label="Email Broadcast" testid="admin-nav-broadcast" />
      <NavItem to="/admin/affiliate" icon={ShoppingBag} label="Affiliate" testid="admin-nav-affiliate" />
    </>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-[#f5f5f5]">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-[#ff7a00]/15 transform transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="px-5 py-5 border-b border-[#ff7a00]/15">
          <Logo size="sm" showSub={false} />
        </div>
        <nav className="p-3 flex flex-col gap-1.5">
          {admin ? adminNav : traderNav}
          {!admin && isAdmin && (
            <div className="pt-3 mt-3 border-t border-[#ff7a00]/15">
              <NavItem to="/admin" icon={ShieldCheck} label="Switch to Admin" testid="nav-switch-admin" />
            </div>
          )}
          {admin && (
            <div className="pt-3 mt-3 border-t border-[#ff7a00]/15">
              <NavItem to="/dashboard" icon={LayoutDashboard} label="Trader View" testid="admin-nav-trader-view" end />
            </div>
          )}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-[#ff7a00]/15">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-[#8a8a8a]">Logged in as</div>
            <div className="text-sm text-[#f5f5f5] truncate">{user?.email}</div>
          </div>
          <Button onClick={() => { logout(); nav("/"); }} variant="ghost" className="w-full justify-start text-[#cfcfcf] hover:text-[#ff7a00] hover:bg-white/5" data-testid="logout-button">
            <LogOut size={17} className="mr-3" /> Log out
          </Button>
        </div>
      </aside>

      {/* Backdrop mobile */}
      {open && <div className="lg:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050505]/80 border-b border-[#ff7a00]/15">
          <div className="px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between">
            <button onClick={() => setOpen(!open)} className="lg:hidden text-[#f5f5f5]" data-testid="sidebar-toggle">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
            <div className="hidden lg:block">
              {admin ? (
                <Badge className="bg-[#ff7a00]/15 text-[#ff7a00] border-[#ff7a00]/30 uppercase tracking-widest text-[10px]">Admin Console</Badge>
              ) : (
                <Badge className="bg-[#ff7a00]/15 text-[#ff7a00] border-[#ff7a00]/30 uppercase tracking-widest text-[10px]">Trader Dashboard</Badge>
              )}
            </div>
            <div className="flex items-center gap-3">
              {!admin && (
                <div className="relative">
                  <button onClick={() => setShowNotifs(!showNotifs)} className="relative p-2 rounded-lg hover:bg-white/5 text-[#cfcfcf]" data-testid="notif-button">
                    <Bell size={18} />
                    {unread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#ff7a00] text-[#050505] rounded-full text-[10px] font-bold flex items-center justify-center">{unread}</span>
                    )}
                  </button>
                  {showNotifs && (
                    <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto glass-strong rounded-xl p-2 z-50" data-testid="notif-panel">
                      <div className="px-3 py-2 text-xs uppercase tracking-widest text-[#8a8a8a] border-b border-[#ff7a00]/10">Notifications</div>
                      {notifs.length === 0 && <div className="p-4 text-sm text-[#8a8a8a]">No notifications yet.</div>}
                      {notifs.map((n) => (
                        <div key={n.id} className={`p-3 rounded-lg ${n.read ? "" : "bg-[#ff7a00]/5"}`} data-testid={`notif-${n.id}`}>
                          <div className="text-sm text-[#f5f5f5] font-semibold">{n.title}</div>
                          <div className="text-xs text-[#8a8a8a] mt-1">{n.body}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="hidden sm:block w-9 h-9 rounded-full bg-[#ff7a00]/15 border border-[#ff7a00]/30 flex items-center justify-center text-[#ff7a00] font-display font-bold" data-testid="user-avatar">
                {(user?.first_name?.[0] || user?.email?.[0] || "T").toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
